import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Statistic,
  Descriptions,
  Tag,
  Radio
} from 'antd';
import {
  ReloadOutlined,
  FileTextOutlined,
  ApiOutlined,
  KeyOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { App as AntApp } from 'antd';
import { admin } from '../../services/api';

const { Title, Text } = Typography;

const IntegracoesDashboard = () => {
  const { message } = AntApp.useApp();
  const [loading, setLoading] = useState(false);
  const [sswConfig, setSswConfig] = useState(null);
  const [sswStatus, setSswStatus] = useState(null);
  const [portalConfig, setPortalConfig] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    await Promise.allSettled([
      admin.getSswConfig().then(setSswConfig).catch(() => setSswConfig(null)),
      admin.sswStatus().then(setSswStatus).catch(() => setSswStatus(null)),
      admin.getPortalNfeConfig().then(setPortalConfig).catch(() => setPortalConfig(null)),
      admin.getOrgIntegrations().then(setIntegrations).catch(() => setIntegrations([])),
      admin.getApiKeys().then(setApiKeys).catch(() => setApiKeys([]))
    ]);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const changeBuscaPath = async (e) => {
    const path = e.target.value;
    try {
      setLoading(true);
      await admin.upsertSswConfig({
        dominio: sswConfig?.dominio || '',
        cpf: sswConfig?.cpf || '',
        usuario: sswConfig?.usuario || '',
        senha: '',
        baseUrl: sswConfig?.baseUrl || 'https://sistema.ssw.inf.br/bin/ssw0422',
        buscaPath: path
      });
      message.success(`Caminho alterado para: ${path === 'database' ? 'Banco Local' : 'SSW Service'}`);
      load();
    } catch (e) {
      message.error(e.message || 'Erro ao alterar caminho');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>Dashboard de Integrações</Title>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Atualizar</Button>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading} size="small">
            <Statistic
              title="Portal Nf-e"
              value={portalConfig ? 'Configurado' : 'Não configurado'}
              valueStyle={{ fontSize: 14, color: portalConfig ? '#52c41a' : '#ff4d4f' }}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading} size="small">
            <Statistic
              title="Integrações"
              value={integrations.length}
              valueStyle={{ fontSize: 14 }}
              prefix={<ApiOutlined />}
              suffix={integrations.length === 1 ? 'ativa' : 'ativas'}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading} size="small">
            <Statistic
              title="API Keys"
              value={apiKeys.length}
              valueStyle={{ fontSize: 14 }}
              prefix={<KeyOutlined />}
              suffix={apiKeys.length === 1 ? 'chave' : 'chaves'}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading} size="small">
            <Statistic
              title="SSW Service"
              value={sswStatus?.estado || (sswConfig ? 'Configurado' : 'Não configurado')}
              valueStyle={{ fontSize: 14, color: sswStatus?.estado === 'LOGADO' ? '#52c41a' : sswConfig ? '#faad14' : '#ff4d4f' }}
              prefix={<CloudServerOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Caminho da Busca de Notas Fiscais" style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Define como a aplicação busca os dados da nota fiscal ao escanear ou digitar a chave de acesso no modal de inclusão.
          </Text>
        </div>
        <Radio.Group value={sswConfig?.buscaPath || 'database'} onChange={changeBuscaPath} disabled={loading}>
          <Radio.Button value="database"><DatabaseOutlined /> Banco Local</Radio.Button>
          <Radio.Button value="ssw"><CloudServerOutlined /> SSW Service</Radio.Button>
        </Radio.Group>
        <div style={{ marginTop: 12 }}>
          {sswConfig?.buscaPath === 'ssw'
            ? <Tag color="blue">Buscando dados via SSW Service — requer navegador logado e configuração na aba SSW</Tag>
            : <Tag color="green">Buscando dados no banco local</Tag>}
        </div>
        {!sswConfig && (
          <div style={{ marginTop: 8 }}>
            <Text type="warning">Nenhuma configuração SSW encontrada. O caminho padrío é "Banco Local".</Text>
          </div>
        )}
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title={<><CloudServerOutlined /> SSW Service</>} size="small" loading={loading}>
            {sswConfig ? (
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Domínio">{sswConfig.dominio}</Descriptions.Item>
                <Descriptions.Item label="Usuário">{sswConfig.usuario}</Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={sswStatus?.estado === 'LOGADO' ? 'green' : 'orange'}>{sswStatus?.estado || 'Desconectado'}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Logado">
                  <Tag color={sswStatus?.logado ? 'green' : 'red'}>{sswStatus?.logado ? 'Sim' : 'Não'}</Tag>
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Text type="secondary">Nenhuma configuração — acesse a aba SSW para configurar.</Text>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<><FileTextOutlined /> Portal Nf-e</>} size="small" loading={loading}>
            {portalConfig ? (
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Endpoint">
                  <Text code style={{ fontSize: 12 }}>{portalConfig.endpointConsultaNfe}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Criado em">
                  {new Date(portalConfig.createdAt).toLocaleString('pt-BR')}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Text type="secondary">Nenhuma configuração — acesse a aba Portal Nf-e para configurar.</Text>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<><ApiOutlined /> Integrações</>} size="small" loading={loading}>
            {integrations.length > 0 ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                {integrations.map((integration, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                    <Space>
                      {integration.isActive
                        ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                      <Text>{integration.name}</Text>
                    </Space>
                    <Tag>{integration.requestsToday || 0}/{integration.maxRequestsPerDay || '∞'} hoje</Tag>
                  </div>
                ))}
              </Space>
            ) : (
              <Text type="secondary">Nenhuma integração cadastrada.</Text>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<><KeyOutlined /> API Keys</>} size="small" loading={loading}>
            {apiKeys.length > 0 ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                {apiKeys.map((key, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                    <Space>
                      {key.isActive
                        ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                      <Text>{key.name}</Text>
                    </Space>
                    <Tag>{key.description ? key.description.substring(0, 30) : '-'}</Tag>
                  </div>
                ))}
              </Space>
            ) : (
              <Text type="secondary">Nenhuma API Key cadastrada.</Text>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default IntegracoesDashboard;
